/* Staff Absences — iter87.
 * RH declares/detects staff absences, then decides justified (paid) or
 * rejected (deducted from payslip). */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  UserMinus, MagnifyingGlass, CheckCircle, XCircle, Trash, Plus,
  ArrowClockwise, CurrencyDollar,
} from '@phosphor-icons/react';
import api from '../lib/api';

const STATUS_META = {
  pending: { label: 'En attente', color: '#B45309', bg: '#FEF3C7' },
  justified: { label: 'Justifiée (payée)', color: '#059669', bg: '#ECFDF5' },
  rejected: { label: 'Non justifiée (retenue)', color: '#B91C1C', bg: '#FEE2E2' },
};

const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function StaffAbsences() {
  const [month, setMonth] = useState(currentMonth());
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);

  // Manual create
  const [createOpen, setCreateOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    kind: 'teacher', employee_id: '', date: new Date().toISOString().slice(0, 10),
    reason: '',
  });

  // Decision dialog (justify/reject)
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionAbsence, setDecisionAbsence] = useState(null);
  const [decisionKind, setDecisionKind] = useState('justify');
  const [decisionReason, setDecisionReason] = useState('');
  const [decisionAttachment, setDecisionAttachment] = useState('');
  const [decisionComment, setDecisionComment] = useState('');

  const range = useMemo(() => {
    const y = parseInt(month.split('-')[0], 10);
    const m = parseInt(month.split('-')[1], 10);
    const first = `${month}-01`;
    const last = new Date(y, m, 0).toISOString().slice(0, 10);
    return { first, last };
  }, [month]);

  const loadEmployees = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.get('/teachers').then(r => r.data),
        api.get('/staff').then(r => r.data),
      ]);
      setEmployees([
        ...t.map(x => ({ ...x, kind: 'teacher' })),
        ...s.map(x => ({ ...x, kind: 'staff' })),
      ]);
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: range.first, to_date: range.last };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (kindFilter !== 'all') params.kind = kindFilter;
      const { data } = await api.get('/staff-absences', { params });
      setItems(data.items || []);
      setKpi(data.kpi || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [range, statusFilter, kindFilter]);

  useEffect(() => { load(); loadEmployees(); }, [load, loadEmployees]);

  const detect = async () => {
    setDetecting(true);
    try {
      const { data } = await api.post('/staff-absences/detect', {
        from_date: range.first, to_date: range.last,
      });
      toast.success(
        `${data.created} absence(s) détectée(s), ${data.skipped_existing} déjà existante(s)`,
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur détection');
    } finally { setDetecting(false); }
  };

  const openDecision = (absence, kind) => {
    setDecisionAbsence(absence);
    setDecisionKind(kind);
    setDecisionReason(absence.reason || '');
    setDecisionAttachment(absence.attachment_url || '');
    setDecisionComment('');
    setDecisionOpen(true);
  };

  const submitDecision = async () => {
    if (decisionKind === 'justify' && !decisionReason.trim()) {
      toast.error('Motif requis pour justifier');
      return;
    }
    try {
      const endpoint = decisionKind === 'justify' ? 'justify' : 'reject';
      await api.put(`/staff-absences/${decisionAbsence.id}/${endpoint}`, {
        reason: decisionReason, attachment_url: decisionAttachment,
        reviewer_comment: decisionComment,
      });
      toast.success(
        decisionKind === 'justify'
          ? 'Absence justifiée — heures payées'
          : 'Absence non justifiée — heures retenues',
      );
      setDecisionOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette absence ?')) return;
    try {
      await api.delete(`/staff-absences/${id}`);
      toast.success('Absence supprimée');
      await load();
    } catch (e) { toast.error('Erreur'); }
  };

  const submitCreate = async () => {
    if (!form.employee_id || !form.date) {
      toast.error('Champs requis manquants'); return;
    }
    try {
      await api.post('/staff-absences', form);
      toast.success('Absence enregistrée');
      setCreateOpen(false);
      setForm({ ...form, employee_id: '', reason: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="staff-absences-page">
      <Toaster position="top-center" richColors />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-[#0F172A] flex items-center gap-2">
            <UserMinus size={28} weight="duotone" className="text-[#0052FF]" />
            Absences du personnel
          </h1>
          <p className="text-sm text-[#64748B] max-w-2xl">
            Justifier les absences pour maintenir le salaire, ou les retenir
            si non justifiées. La paie est ajustée automatiquement.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs text-[#64748B]">Mois</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="w-40 h-9" data-testid="sa-month-input" />
          </div>
          <div>
            <Label className="text-xs text-[#64748B]">Statut</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-9" data-testid="sa-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="justified">Justifiées</SelectItem>
                <SelectItem value="rejected">Non justifiées</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#64748B]">Type</Label>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-32 h-9" data-testid="sa-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="teacher">Enseignants</SelectItem>
                <SelectItem value="staff">Personnel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} data-testid="sa-refresh">
            <ArrowClockwise size={14} className="mr-1" /> Actualiser
          </Button>
          <Button variant="outline" onClick={() => {
            window.open(
              `${process.env.REACT_APP_BACKEND_URL}/api/staff-absences/bordereau.pdf?month=${month}`,
              '_blank');
          }} data-testid="sa-bordereau-btn" title="Bordereau signable des retenues">
            📄 Bordereau PDF
          </Button>
          <Button variant="outline" onClick={() => {
            window.open(
              `${process.env.REACT_APP_BACKEND_URL}/api/staff-absences/bordereau.xlsx?month=${month}`,
              '_blank');
          }} data-testid="sa-bordereau-xlsx-btn" title="Bordereau retenues au format Excel">
            📊 Bordereau Excel
          </Button>
          <Button variant="outline" onClick={() => {
            window.open(
              `${process.env.REACT_APP_BACKEND_URL}/api/staff-absences/bordereau.csv?month=${month}`,
              '_blank');
          }} data-testid="sa-bordereau-csv-btn" title="Bordereau retenues CSV (Sage/Ciel)">
            📥 Bordereau CSV
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const { data } = await api.post('/staff-absences/notify-pending-employees',
                { month, preview: true });
              const lines = (data.recipients || []).map(r =>
                `• ${r.employee_name} (${r.absences_count} abs.) — ${r.email || 'pas d email'} · ${r.phone || 'pas de tel'}`
              ).join('\n');
              window.alert(
                `PRÉVISUALISATION J-2 — ${data.employees_targeted} employé(s)\n\n`
                + (lines || 'Aucun destinataire')
                + '\n\nMessage exemple:\n' + (data.recipients?.[0]?.message_preview || '—')
              );
            } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
          }} data-testid="sa-notify-preview-btn" title="Prévisualiser sans envoyer">
            👁 Aperçu J-2
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const { data } = await api.post('/staff-absences/notify-pending-employees', { month });
              toast.success(
                `${data.employees_targeted} employé(s) notifié(s) — ${data.sent_email} email, ${data.sent_sms} SMS`,
              );
              await load();
            } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
          }} data-testid="sa-notify-j2-btn" title="Rappel J-2 aux salariés avec absences en attente">
            📧 Rappel J-2 salariés
          </Button>
          {/* iter93/94 — Manual trigger for the J-3 employee reminder cron.
              iter94: dedupe within 24 h + optional force override. */}
          <Button variant="outline" onClick={async () => {
            try {
              const { data } = await api.get(
                `/staff-absences/j3-reminder/preview?month=${month}`);
              const lines = (data.recipients || []).slice(0, 15).map(r =>
                `• ${r.name} — ${r.count} abs. — ${Math.round(r.potential_fcfa).toLocaleString('fr-FR')} FCFA — ${r.phone ? '📱' : '❌tel'} ${r.email ? '📧' : '❌email'}`
              ).join('\n');
              const extra = data.recipients.length > 15
                ? `\n… et ${data.recipients.length - 15} autre(s)` : '';
              // eslint-disable-next-line no-alert
              const ok = window.confirm(
                `APERÇU J-3 (${data.recipients_count} employé(s))\n\n${lines || 'Aucun destinataire'}${extra}\n\nEnvoyer les SMS + emails maintenant ?`
              );
              if (!ok) return;
              let r2 = (await api.post(
                `/staff-absences/j3-reminder/run?month=${month}`)).data;
              if (r2.skipped) {
                // eslint-disable-next-line no-alert
                const forceIt = window.confirm(
                  `⚠️ ${r2.reason}\n\nDernier envoi : ${r2.last_run_at}\n` +
                  `Destinataires : ${r2.recipients_count}, SMS: ${r2.sms_sent}, Emails: ${r2.email_sent}\n\n` +
                  `Forcer un nouvel envoi maintenant ?`);
                if (!forceIt) return;
                r2 = (await api.post(
                  `/staff-absences/j3-reminder/run?month=${month}&force=1`)).data;
              }
              toast.success(
                `J-3 : ${r2.recipients_count} employé(s) — ${r2.sms_sent} SMS, ${r2.email_sent} email(s)`);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erreur');
            }
          }} data-testid="sa-j3-employee-btn"
             title="Rappel J-3 aux employés (SMS + email) pour justifier avant clôture paie">
            📱 J-3 employés
          </Button>
          {/* iter94 — J-3 history quick view */}
          <Button variant="outline" onClick={async () => {
            try {
              const { data } = await api.get('/staff-absences/j3-reminder/history?limit=10');
              const items = data.items || [];
              if (!items.length) {
                toast.info('Aucun envoi J-3 enregistré pour l\'instant.');
                return;
              }
              const lines = items.map(i => {
                const d = new Date(i.created_at).toLocaleString('fr-FR');
                return `• ${d} — ${i.month} — ${i.recipients_count} dest. — ${i.sms_sent} SMS / ${i.email_sent} emails${i.forced ? ' (forcé)' : ''} — par ${i.triggered_by}`;
              }).join('\n');
              // eslint-disable-next-line no-alert
              window.alert(`Historique J-3 (10 derniers)\n\n${lines}`);
            } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
          }} data-testid="sa-j3-history-btn"
             title="Historique des envois J-3">
            🕓 Historique J-3
          </Button>
          <Button onClick={detect} disabled={detecting} data-testid="sa-detect-btn"
            className="bg-[#0052FF] hover:bg-[#0044CC]">
            <MagnifyingGlass size={14} className="mr-1" />
            {detecting ? 'Détection…' : 'Détecter depuis biométrie'}
          </Button>
          <Button variant="outline" onClick={() => setCreateOpen(true)}
            data-testid="sa-add-btn">
            <Plus size={14} className="mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-3">
            <p className="text-[10px] uppercase text-[#64748B] font-bold">Total</p>
            <p className="text-2xl font-outfit font-bold" data-testid="sa-kpi-total">{kpi.total}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#FEF3C7]">
            <p className="text-[10px] uppercase font-bold text-amber-800">En attente</p>
            <p className="text-2xl font-outfit font-bold text-amber-800" data-testid="sa-kpi-pending">{kpi.pending}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#ECFDF5]">
            <p className="text-[10px] uppercase font-bold text-emerald-800">Justifiées</p>
            <p className="text-2xl font-outfit font-bold text-emerald-700">{kpi.justified}</p>
            <p className="text-[10px] text-emerald-700/70">{kpi.hours_justified}h payées</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#FEE2E2]">
            <p className="text-[10px] uppercase font-bold text-red-800">Retenues</p>
            <p className="text-2xl font-outfit font-bold text-red-700">{kpi.rejected}</p>
            <p className="text-[10px] text-red-700/70">{kpi.hours_deducted}h non payées</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#EEF2FF]">
            <p className="text-[10px] uppercase font-bold text-blue-800 flex items-center gap-1">
              <CurrencyDollar size={12} weight="bold" /> Retenue estimée
            </p>
            <p className="text-2xl font-outfit font-bold text-blue-700" data-testid="sa-kpi-deduction">
              {(kpi.deduction_fcfa || 0).toLocaleString()} <span className="text-xs">FCFA</span>
            </p>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          {loading ? (
            <p className="text-center py-10 text-[#94A3B8]">Chargement…</p>
          ) : items.length === 0 ? (
            <p className="text-center py-10 text-[#64748B]">
              Aucune absence pour ce filtre. Cliquez "Détecter depuis biométrie"
              pour scanner les journées manquées automatiquement.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="sa-table">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Employé</th>
                  <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Type</th>
                  <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Date</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Heures</th>
                  <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Statut</th>
                  <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Motif</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const sm = STATUS_META[a.status];
                  return (
                    <tr key={a.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]"
                        data-testid={`sa-row-${a.id}`}>
                      <td className="py-2 px-2 font-medium">{a.employee_name}</td>
                      <td className="py-2 px-2 text-center text-xs">
                        {a.kind === 'teacher' ? '👨‍🏫 Enseignant' : '👷 Personnel'}
                      </td>
                      <td className="py-2 px-2 text-center">{a.date}</td>
                      <td className="py-2 px-2 text-right font-semibold">
                        {a.hours_missed || 0}h
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: sm.bg, color: sm.color }}>
                          {sm.label}
                        </span>
                        {/* iter92 — Badge signalant une justification employé */}
                        {a.employee_justified && a.status === 'pending' && (
                          <span
                            data-testid={`sa-employee-badge-${a.id}`}
                            title={a.attachment_name
                              ? `Justification + PJ envoyée par ${a.employee_name}`
                              : `Justification envoyée par ${a.employee_name}`}
                            className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-300"
                          >
                            ✉️{a.attachment_name ? '📎' : ''}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs text-[#475569] max-w-xs truncate">
                        {a.reason || '—'}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <div className="inline-flex gap-1">
                          {a.status !== 'justified' && (
                            <Button size="sm" variant="ghost" onClick={() => openDecision(a, 'justify')}
                              className="text-emerald-700 hover:bg-emerald-50"
                              data-testid={`sa-justify-${a.id}`} title="Justifier">
                              <CheckCircle size={16} weight="fill" />
                            </Button>
                          )}
                          {a.status !== 'rejected' && (
                            <Button size="sm" variant="ghost" onClick={() => openDecision(a, 'reject')}
                              className="text-red-700 hover:bg-red-50"
                              data-testid={`sa-reject-${a.id}`} title="Refuser">
                              <XCircle size={16} weight="fill" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => remove(a.id)}
                            data-testid={`sa-del-${a.id}`} title="Supprimer">
                            <Trash size={14} className="text-slate-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="sa-create-dialog">
          <DialogHeader>
            <DialogTitle>Déclarer une absence</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.kind}
                onValueChange={(v) => setForm({ ...form, kind: v, employee_id: '' })}>
                <SelectTrigger data-testid="sa-create-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher">Enseignant</SelectItem>
                  <SelectItem value="staff">Personnel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Employé *</Label>
              <Select value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="sa-create-employee"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.kind === form.kind).map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.first_name} {e.last_name} — {e.function || e.subject || e.staff_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="sa-create-date" />
            </div>
            <div>
              <Label>Motif</Label>
              <Textarea rows={2} value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Maladie, mission, formation…"
                data-testid="sa-create-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={submitCreate} data-testid="sa-create-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision dialog */}
      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent data-testid="sa-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decisionKind === 'justify' ? 'Justifier l\'absence' : 'Refuser la justification'}
            </DialogTitle>
          </DialogHeader>
          {decisionAbsence && (
            <div className="space-y-3">
              <div className="text-xs bg-[#F8FAFC] p-2 rounded border border-[#E2E8F0]">
                <p><strong>{decisionAbsence.employee_name}</strong> — {decisionAbsence.date}</p>
                <p>{decisionAbsence.hours_missed}h manquées</p>
              </div>
              {/* iter92 — Show employee-submitted justification info if any */}
              {(decisionAbsence?.employee_justified || decisionAbsence?.attachment_name) && (
                <div className="text-xs bg-blue-50 border border-blue-200 rounded p-3 text-blue-900 space-y-1"
                  data-testid="sa-employee-submission">
                  <div>
                    <b>✉️ Justification envoyée par l'employé</b>
                  </div>
                  {decisionAbsence.reason && (
                    <div>Motif : {decisionAbsence.reason}</div>
                  )}
                  {decisionAbsence.attachment_name && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/staff-absences/${decisionAbsence.id}/attachment`}
                      target="_blank" rel="noreferrer"
                      className="text-[#0052FF] hover:underline inline-flex items-center gap-1"
                      data-testid="sa-view-employee-attachment"
                    >
                      📎 Voir la pièce jointe ({decisionAbsence.attachment_name})
                    </a>
                  )}
                </div>
              )}
              {decisionKind === 'justify' ? (
                <>
                  <div>
                    <Label>Motif *</Label>
                    <Textarea rows={2} value={decisionReason}
                      onChange={(e) => setDecisionReason(e.target.value)}
                      placeholder="Maladie (avec certificat), mission externe, formation…"
                      data-testid="sa-decision-reason" />
                  </div>
                  <div>
                    <Label>Pièce jointe (URL certificat, PDF…)</Label>
                    <Input value={decisionAttachment}
                      onChange={(e) => setDecisionAttachment(e.target.value)}
                      placeholder="https://…"
                      data-testid="sa-decision-attachment" />
                  </div>
                </>
              ) : (
                <div className="text-xs bg-red-50 border border-red-200 rounded p-3 text-red-800">
                  ⚠ L'heure(s) manquée(s) sera(ont) retenue(s) sur la paie
                  du mois.
                </div>
              )}
              <div>
                <Label>Commentaire RH (facultatif)</Label>
                <Textarea rows={2} value={decisionComment}
                  onChange={(e) => setDecisionComment(e.target.value)}
                  data-testid="sa-decision-comment" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionOpen(false)}>Annuler</Button>
            <Button onClick={submitDecision}
              className={decisionKind === 'justify'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-red-600 hover:bg-red-700'}
              data-testid="sa-decision-submit">
              {decisionKind === 'justify' ? 'Justifier (payer)' : 'Refuser (retenir)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
